import type { CSSProperties, FC } from 'react';
import React from 'react';
import styled from 'styled-components';
import type { XAxisLabel } from '../heat-map/Types';
import { OptionIconSVG } from '../common/OptionIconSVG';
import { calculateMaxCharacters } from 'venn-utils';

export const X_AXIS_HEIGHT = 242;

interface XAxisProps {
  ticks: XAxisLabel[];
  cellWidth: number;
  height: number;
  offset: number;
  bottomHeight: number;
  angle: number;
  labelFormatter?: (tick: XAxisLabel) => CSSProperties;
  print?: boolean;
}

const COL_SHIFT = 0.5;
const defaultFormatter = () => ({});

const StyledText = styled.text<{ text: string; weight: number | 'bold' | 'normal'; print?: boolean }>`
  font-family: ${(props) => props.theme.Typography.fontFamily};
  font-size: ${(props) => (props.print ? '10px' : '14px')};
  font-style: normal;
  font-weight: ${(props) => props.weight};
`;

const XAxis: FC<React.PropsWithChildren<XAxisProps>> = ({
  ticks,
  cellWidth,
  offset,
  angle,
  height,
  bottomHeight,
  labelFormatter = defaultFormatter,
  print,
}) => {
  return (
    <>
      {ticks.map((tick, i) => {
        let text = tick.name;
        const optionIconSpace = tick.optionData ? 16 : 0;
        const availableSpace = height - bottomHeight - optionIconSpace;
        const characterLimit = calculateMaxCharacters(text, availableSpace, {
          fontSize: print ? '10px' : '14px',
          fontFamily: 'Roboto',
          fontWeight: 'normal',
        });
        const { type } = tick;
        if (text.length > characterLimit) {
          text = `${text.substr(0, characterLimit - 3)}...`;
        }
        const x = offset + (i + COL_SHIFT) * cellWidth;
        const y = height - bottomHeight;
        const weight = type === 'PORTFOLIO' ? 'bold' : 'normal';
        return (
          <g key={tick.name}>
            <StyledText
              transform={`translate(${x},${y})rotate(${angle})`}
              textAnchor="start"
              text={text}
              className="qa-xaxis"
              weight={weight}
              style={labelFormatter(tick)}
              print={print}
            >
              {tick.optionData && (
                <>
                  <OptionIconSVG optionType={tick.optionData.optionType} />
                  <tspan> </tspan>
                </>
              )}
              {text}
            </StyledText>
          </g>
        );
      })}
    </>
  );
};

export default XAxis;
